import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Button, Tag, Typography, theme } from '@robinpowered/ui-kit';
import InfoSolid from '@robinpowered/ui-kit-icons/InfoSolid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SparkleSolid from '@robinpowered/ui-kit-icons/SparkleSolid';
import { Colors } from '@robinpowered/design-system';
import { faChairOffice } from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { EventCreationType } from 'generated/graphql';
import { ReservationActionsMenu } from './ReservationActionsMenu';
import { ConfirmedCheck } from './ConfirmedCheck';
import { CheckInStatusTag } from './CheckInStatusTag';
import {
  ProfileReservation,
  ReservationMenuAction,
  getReservationActions,
  isAssignedReservationReleasedToday,
} from '../../utils/getReservationActions';
import {
  formatReservationTimeRange,
  getScheduledDaysFromRecurrence,
} from '../../utils/formatBookings';
import { useDeskAllowsIssueReports } from '../../hooks/useDeskAllowsIssueReports';

type ReservationCardProps = {
  reservation: ProfileReservation;
  recurrence: string | null;
  isCurrentPerson: boolean;
  currentTime: moment.Moment;
  onCardClick: (reservation: ProfileReservation) => void;
  onCheckIn: (reservation: ProfileReservation) => void;
  onMenuAction: (
    action: ReservationMenuAction,
    reservation: ProfileReservation
  ) => void;
  /**
   * In the fullscreen Desks panel the card is wide, so actions sit inline
   * with the desk details (right-aligned). In the narrow sider they render as
   * a row of small buttons below the desk details.
   */
  wideLayout?: boolean;
  /**
   * Overrides the default meta line. The fullscreen Desks panel labels each
   * flex reservation with its date (e.g. "Tue, Apr 7, 9:00 am - 5:30 pm")
   * rather than the sider's "Today" range.
   */
  metaLabel?: string;
};

export const ReservationCard = ({
  reservation,
  recurrence,
  isCurrentPerson,
  currentTime,
  onCardClick,
  onCheckIn,
  onMenuAction,
  wideLayout = false,
  metaLabel,
}: ReservationCardProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar');
  const { token } = theme.useToken();

  const { seat } = reservation;
  const { allowed: canReportIssue } = useDeskAllowsIssueReports(seat.id);

  const actions = useMemo(
    () =>
      getReservationActions(
        reservation,
        isCurrentPerson,
        canReportIssue,
        currentTime
      ),
    [reservation, isCurrentPerson, canReportIssue, currentTime]
  );

  const isReleasedToday = useMemo(
    () => isAssignedReservationReleasedToday(reservation, currentTime),
    [reservation, currentTime]
  );

  const scheduleDays = useMemo(() => {
    const days = getScheduledDaysFromRecurrence(recurrence);
    if (days.length === 7) {
      return t('bookings.everyDay');
    }
    return days.map((day) => t(`bookings.daysShort.${day}`)).join(', ');
  }, [recurrence, t]);

  const timeRange = useMemo(
    () =>
      formatReservationTimeRange(
        reservation.startTime,
        reservation.endTime,
        reservation.timeZone
      ),
    [reservation.startTime, reservation.endTime, reservation.timeZone]
  );

  const deskName = seat.zone?.name
    ? t('bookings.deskWithZone', { desk: seat.name, zone: seat.zone.name })
    : seat.name;

  const floorLocation = seat.level?.name
    ? t('bookings.floorLocation', {
        floor: seat.level.name,
        location: seat.location.name,
      })
    : seat.location.name;

  const isAutoBooked =
    actions.kind !== 'assignment' &&
    reservation.creationType === EventCreationType.Automatic;

  const bottomActions = actions.menuActions.filter(isBottomAction);
  const overflowActions = actions.menuActions.filter(
    (action) => !isBottomAction(action)
  );
  const hasActionRow =
    actions.showCheckIn ||
    bottomActions.length > 0 ||
    overflowActions.length > 0;
  const cancelButtonLabel = isReservationInProgress(reservation, currentTime)
    ? t('bookings.menu.end')
    : t('bookings.menu.cancel');

  const actionButtons = (
    <>
      {actions.showCheckIn && (
        <ActionButton
          type="primary"
          size={wideLayout ? 'middle' : 'small'}
          onClick={(event) => {
            event.stopPropagation();
            onCheckIn(reservation);
          }}
        >
          {t('bookings.checkIn')}
        </ActionButton>
      )}
      {bottomActions.map((action) => (
        <ActionButton
          key={action}
          type="default"
          size={wideLayout ? 'middle' : 'small'}
          danger={action === 'cancel'}
          onClick={(event) => {
            event.stopPropagation();
            onMenuAction(action, reservation);
          }}
        >
          {action === 'cancel'
            ? cancelButtonLabel
            : t(`bookings.menu.${action}`)}
        </ActionButton>
      ))}
      {overflowActions.length > 0 && (
        <ReservationActionsMenu
          actions={overflowActions}
          onAction={(action) => onMenuAction(action, reservation)}
        />
      )}
    </>
  );

  return (
    <Card $borderColor={token.colorBorderSecondary}>
      <Meta>
        {actions.kind === 'assignment' ? (
          wideLayout ? (
            <Typography.Text type="secondary">
              {scheduleDays
                ? t('bookings.assignedWithDays', { days: scheduleDays })
                : t('bookings.assigned')}
            </Typography.Text>
          ) : (
            <>
              <AssignedTag>{t('bookings.assigned')}</AssignedTag>
              {scheduleDays && (
                <Typography.Text type="secondary">
                  {scheduleDays}
                </Typography.Text>
              )}
            </>
          )
        ) : (
          <>
            <Typography.Text type="secondary">
              {metaLabel ?? t('bookings.todayRange', { range: timeRange })}
            </Typography.Text>
            {isAutoBooked &&
              (wideLayout ? (
                <AutoBookedTag>
                  <AutoBookedSparkle>
                    <SparkleSolid />
                  </AutoBookedSparkle>
                  <Typography.Text>{t('bookings.autoBooked')}</Typography.Text>
                </AutoBookedTag>
              ) : (
                // The narrow sider is too tight for the full pill, so it
                // compacts down to just the sparkle.
                <AutoBookedTag
                  $compact
                  role="img"
                  aria-label={t('bookings.autoBooked')}
                  title={t('bookings.autoBooked')}
                >
                  <AutoBookedSparkle>
                    <SparkleSolid />
                  </AutoBookedSparkle>
                </AutoBookedTag>
              ))}
          </>
        )}
      </Meta>

      <Body>
        <DeskIconWrapper
          $background={token.colorFillSecondary}
          $color={token.colorIcon}
        >
          <FontAwesomeIcon icon={faChairOffice} />
          {actions.isCheckedIn && (
            <CheckOverlay>
              <ConfirmedCheck size={14} />
            </CheckOverlay>
          )}
        </DeskIconWrapper>
        <DeskText>
          <DeskNameButton
            type="button"
            onClick={() => onCardClick(reservation)}
            aria-label={t('bookings.viewDeskLabel', { desk: deskName })}
          >
            {deskName}
          </DeskNameButton>
          <Typography.Text type="secondary">{floorLocation}</Typography.Text>
        </DeskText>
        {/* Fullscreen Desks panel: actions sit inline with the desk details,
            right-aligned, with the overflow menu as the last action. */}
        {wideLayout && hasActionRow && (
          <BodyActions>{actionButtons}</BodyActions>
        )}
      </Body>

      {isReleasedToday && (
        <ReleasedNotice>
          <ReleasedIcon>
            <InfoSolid />
          </ReleasedIcon>
          <Typography.Text type="secondary">
            {t('bookings.sharedToday')}
          </Typography.Text>
        </ReleasedNotice>
      )}

      {!wideLayout && <CheckInStatusTag status={actions.checkInStatus} />}

      {/* Narrow sider: one wrapped row of small auto-width buttons below the
          desk details, with the overflow menu as the last action. */}
      {!wideLayout && hasActionRow && <ActionRow>{actionButtons}</ActionRow>}
    </Card>
  );
};

type BottomAction = Extract<
  ReservationMenuAction,
  'edit' | 'release' | 'cancel'
>;

const isBottomAction = (
  action: ReservationMenuAction
): action is BottomAction =>
  action === 'edit' || action === 'release' || action === 'cancel';

const isReservationInProgress = (
  reservation: ProfileReservation,
  currentTime: moment.Moment
): boolean => {
  const zone =
    reservation.timeZone ?? reservation.seat.location.timezone ?? 'UTC';
  const zonedNow = currentTime.clone().tz(zone);
  return (
    moment.tz(reservation.startTime, zone).isSameOrBefore(zonedNow) &&
    (!reservation.endTime ||
      moment.tz(reservation.endTime, zone).isAfter(zonedNow))
  );
};

const Card = styled.div<{ $borderColor: string }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 8px;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
`;

const AssignedTag = styled(Tag)`
  margin: 0;
`;

// The "Auto-booked" pill: the same magenta sparkle treatment as the schedule
// app's suggested-spaces pill (ReservationsEventsWithoutSpaces). It pushes to
// the meta row's far right; $compact drops the text down to just the sparkle
// for the narrow sider.
const AutoBookedTag = styled.span<{ $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: ${({ $compact }) => ($compact ? '4px' : '4px 8px')};
  border-radius: 8px;
  background-color: ${Colors.Magenta5};
`;

const AutoBookedSparkle = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 16px;
  color: ${Colors.Magenta100};
`;

const Body = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

// Right-aligns the wide layout's action buttons beside the desk details.
const BodyActions = styled.div`
  display: flex;
  align-items: center;
  align-self: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
`;

const DeskIconWrapper = styled.span<{ $background: string; $color: string }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  flex-shrink: 0;
  font-size: 18px;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
`;

const CheckOverlay = styled.span`
  position: absolute;
  bottom: -4px;
  right: -4px;
  display: inline-flex;
`;

const DeskText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  word-break: break-word;
`;

// Only the desk name navigates to the desk on the map; the card itself is not
// interactive. Rendered as a button that reads as plain text until hovered.
const DeskNameButton = styled.button`
  align-self: flex-start;
  max-width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 16px;
  color: inherit;
  text-align: left;
  word-break: break-word;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: var(--ant-color-link, var(--ant-color-primary, #be1f77));
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--ant-color-primary, #eb2f96);
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

const ReleasedNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ReleasedIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  color: var(--ant-color-text-secondary);
  font-size: 14px;
`;

const ActionButton = styled(Button)`
  min-width: 0;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
